import store, { persistor } from "../../Redux_Toolkit/store";
import { clearTokenRefresh } from "../tokenRefresh";
import { logoutUser } from "../../Redux_Toolkit/Auth/AuthThunk";
import { clearActiveDraftSession } from "../activeDraftSession";
import { clearDraftImageFile } from "../draftImageSession";
import { clearAllEditSessions, clearAllEditImages } from "../dealEditSession";

/**
 * Centralized Auth Utility
 * Handles login state, logout, and session management
 */

/**
 * Check if user is currently logged in
 * @returns {boolean}
 */
export const isAuthenticated = () => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");
    return !!(token && user);
};

/**
 * Get current user from localStorage
 * @returns {object|null}
 */
export const getCurrentUser = () => {
    try {
        const user = localStorage.getItem("user");
        return user ? JSON.parse(user) : null;
    } catch {
        return null;
    }
};

/**
 * Normalize and store user data in localStorage
 * @param {object} userData
 */
export const setUserData = (userData = {}) => {
    const normalized = {
        ...userData,
        name: userData?.name?.trim() || null,
        email: userData?.email?.trim() || null,
        phone: userData?.phone?.trim() || null,
        profileImage: userData?.profileImage?.trim() || null,
    };

    localStorage.setItem("user", JSON.stringify(normalized));
};

/**
 * Get auth token from localStorage
 * @returns {string|null}
 */
export const getToken = () => {
    return localStorage.getItem("token");
};

/**
 * Get refresh token from localStorage
 * @returns {string|null}
 */
export const getRefreshToken = () => {
    return localStorage.getItem("refreshToken");
};

/**
 * Clears auth-related localStorage values while preserving app bridge keys
 */
export const clearLocalStorageSafe = () => {
    const isMobileApp = localStorage.getItem("isMobileApp");

    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("merchantId");
    localStorage.removeItem("influencerId");
    localStorage.removeItem("profileComplete");
    localStorage.removeItem("fcmToken");
    localStorage.removeItem("fcmPlatform");
    // NOTE: intentionally do NOT remove "__impersonating__" here — the logout
    // flow checks it AFTER this wipe to decide whether to close the tab, then
    // endImpersonationTab() clears it.

    // Drop in-flight deal create + edit sessions to prevent cross-tenant leak.
    clearActiveDraftSession();
    void clearDraftImageFile();
    clearAllEditSessions();
    void clearAllEditImages();

    sessionStorage.clear();

    if (isMobileApp) {
        localStorage.setItem("isMobileApp", isMobileApp);
    }

    // Notify listeners (e.g. impersonation banner) that auth storage changed,
    // so they re-evaluate even on same-tab client-side navigation.
    if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("user:updated"));
    }
};

/**
 * If an admin was impersonating in this tab, wipe the impersonation flag and
 * close the tab. Returns true when impersonation was active (tab close attempted),
 * false for a normal session (caller should redirect as usual).
 */
export const endImpersonationTab = () => {
    if (!localStorage.getItem("__impersonating__")) return false;
    localStorage.removeItem("__impersonating__");
    window.close();
    return true;
};

/**
 * Complete logout - clears both localStorage AND Redux state
 * Use this for:
 * - Logout button click
 * - 401 Unauthorized response
 * - Session expired
 *
 * @param {boolean} redirect - Whether to redirect to signin page
 * @param {string} redirectPath - Path to redirect to (default: /login)
 * @param {boolean} skipApiCall - Skip logout API call (use when tokens are already expired)
 */
export const logout = async (redirect = true, redirectPath = "/login", skipApiCall = false) => {
    try {
        // 0. Call backend logout API first (skip if tokens are already expired)
        if (!skipApiCall) {
            try {
                await store.dispatch(logoutUser()).unwrap();
            } catch (apiError) {
                // API call failed — still proceed with local cleanup
                console.warn("Logout API call failed, proceeding with local cleanup:", apiError);
            }
        }

        // 1. Clear token refresh timer
        clearTokenRefresh();

        // 2. Clear localStorage keys
        clearLocalStorageSafe();

        // 3. Purge redux-persist storage (clears persist:sumoEatsMerchant)
        await persistor.purge();

        // 4. Reset Redux store state by dispatching logout action
        // This clears the in-memory state immediately
        const { logout: logoutAction } = await import("../../Redux_Toolkit/Auth/AuthSlice");
        store.dispatch(logoutAction());

        // 5. Clear merchant data from Redux store
        const { clearMerchantProfile } = await import("../../Redux_Toolkit/Merchant/MerchantSlice");
        store.dispatch(clearMerchantProfile());

        // 6. If impersonating → close tab (helper wipes flag). Else normal redirect.
        if (endImpersonationTab()) {
            // Some browsers block close() for non-script-opened tabs — fall back to redirect.
            setTimeout(() => {
                if (redirect) window.location.href = redirectPath;
            }, 150);
        } else if (redirect) {
            window.location.href = redirectPath;
        }
    } catch (error) {
        console.error("Logout error:", error);
        // Fallback: force clear and redirect
        clearLocalStorageSafe();
        localStorage.removeItem("persist:sumoEatsMerchant");
        localStorage.removeItem("persist:sumoEatsMerchantAuth");
        if (endImpersonationTab()) {
            setTimeout(() => {
                if (redirect) window.location.href = redirectPath;
            }, 150);
        } else if (redirect) {
            window.location.href = redirectPath;
        }
    }
};

/**
 * Handle session expired (401 error)
 * Shows message and logs out (skips API call since tokens are already expired)
 */
export const handleSessionExpired = () => {
    logout(true, "/login", true);
};

// export default {
//     isAuthenticated,
//     getCurrentUser,
//     getToken,
//     logout,
//     handleSessionExpired,
// };
